import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControlLabel,
  Switch,
  Typography,
  Box,
  Card,
  CardContent,
  Divider,
  CircularProgress,
  Alert,
  Snackbar,
  Grid,
  IconButton,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction
} from '@mui/material';
import { Settings, Bell, Mail, MessageSquare, Smartphone, Monitor, Trash2 } from 'lucide-react';
import { urlBase64ToUint8Array } from '../../utils/utils';

// Generate or retrieve a unique device ID for this browser
const getDeviceId = () => {
  const DEVICE_ID_KEY = 'push_device_id';
  let deviceId = localStorage.getItem(DEVICE_ID_KEY);
  if (!deviceId) {
    // Generate a unique ID using crypto API or fallback
    if (window.crypto && window.crypto.randomUUID) {
      deviceId = window.crypto.randomUUID();
    } else {
      // Fallback for older browsers
      deviceId = 'device_' + Date.now() + '_' + Math.random().toString(36).substring(2, 15);
    }
    localStorage.setItem(DEVICE_ID_KEY, deviceId);
  }
  return deviceId;
};

// Get a human-readable device name from user agent
const getDeviceName = () => {
  const ua = navigator.userAgent;
  let browser = 'Browser';
  let os = 'Unknown';
  
  // Detect browser
  if (ua.includes('Chrome') && !ua.includes('Edg')) browser = 'Chrome';
  else if (ua.includes('Edg')) browser = 'Edge';
  else if (ua.includes('Firefox')) browser = 'Firefox';
  else if (ua.includes('Safari') && !ua.includes('Chrome')) browser = 'Safari';
  
  // Detect OS
  if (ua.includes('Windows')) os = 'Windows';
  else if (ua.includes('Mac OS')) os = 'macOS';
  else if (ua.includes('Linux')) os = 'Linux';
  else if (ua.includes('Android')) os = 'Android';
  else if (ua.includes('iPhone') || ua.includes('iPad')) os = 'iOS';
  
  return `${browser} on ${os}`;
};

const NotificationSettings = ({ open, onClose, darkMode }) => {
  const [apps, setApps] = useState([]);
  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState(null);
  const [hasPushSubscription, setHasPushSubscription] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info'
  });
  const [subscribing, setSubscribing] = useState(false);
  const [devices, setDevices] = useState([]);
  const [loadingDevices, setLoadingDevices] = useState(false);
  const [deletingDevice, setDeletingDevice] = useState(null);
  const currentDeviceId = getDeviceId();

  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const normalizeSettingsPayload = (payload) => {
    if (Array.isArray(payload)) return payload;
    if (!payload) return [];
    if (Array.isArray(payload.results)) return payload.results;
    if (Array.isArray(payload.settings)) return payload.settings;
    console.warn('[NotificationSettings] unexpected settings payload:', payload);
    return [];
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      
      if (!token) {
        throw new Error('Token di autenticazione non trovato');
      }

      // Try to fetch available apps
      let appsData = { apps: [] };
      try {
        const appsResponse = await fetch(`${process.env.REACT_APP_DOMAIN}/notifications/api/settings/apps/`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (appsResponse.ok) {
          appsData = await appsResponse.json();
        } else {
          console.warn('Apps endpoint not available, using default apps');
          // Fallback to default apps if endpoint doesn't exist
          appsData = {
            apps: [
              { value: 'crm', label: 'CRM' },
              { value: 'ai_app_new_chat', label: 'New Chat' },
              { value: 'ai_app_new_message', label: 'New Message' },
              { value: 'crm_new_lead', label: 'New Lead' }
            ]
          };
        }
      } catch (appsError) {
        console.warn('Error fetching apps:', appsError);
        // Use default apps if there's an error
        appsData = {
          apps: [
            { value: 'crm', label: 'CRM' },
            { value: 'ai_app_new_chat', label: 'New Chat' },
            { value: 'ai_app_new_message', label: 'New Message' },
            { value: 'crm_new_lead', label: 'New Lead' }
          ]
        };
      }

      setApps(appsData.apps || []);

      // Try to fetch current settings
      let settingsData = [];
      try {
        const settingsResponse = await fetch(`${process.env.REACT_APP_DOMAIN}/notifications/api/settings/`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

      if (settingsResponse.ok) {
        settingsData = await settingsResponse.json();
        settingsData = normalizeSettingsPayload(settingsData);
      } else {
          console.warn('Settings endpoint returned:', settingsResponse.status);
          // If settings don't exist yet, start with empty array
          settingsData = [];
        }
      } catch (settingsError) {
        console.warn('Error fetching settings:', settingsError);
        settingsData = [];
      }

      setSettings(Array.isArray(settingsData) ? settingsData : []);
      
    } catch (err) {
      console.error('Fetch data error:', err);
      setError(err.message);
      showSnackbar(err.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchDevices = async () => {
    try {
      setLoadingDevices(true);
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await fetch(`${process.env.REACT_APP_DOMAIN}/notifications/api/devices/`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        // Mark current device
        const devicesWithCurrent = (data.devices || []).map(device => ({
          ...device,
          is_current: device.device_id === currentDeviceId
        }));
        setDevices(devicesWithCurrent);
      }
    } catch (err) {
      console.error('Error fetching devices:', err);
    } finally {
      setLoadingDevices(false);
    }
  };

  const handleDeleteDevice = async (deviceDbId) => {
    try {
      setDeletingDevice(deviceDbId);
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await fetch(`${process.env.REACT_APP_DOMAIN}/notifications/api/devices/${deviceDbId}/`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        showSnackbar('Dispositivo rimosso', 'success');
        setDevices(prev => prev.filter(d => d.id !== deviceDbId));
      } else {
        const err = await response.json();
        showSnackbar(err.error || 'Errore nella rimozione', 'error');
      }
    } catch (err) {
      showSnackbar('Errore nella rimozione del dispositivo', 'error');
    } finally {
      setDeletingDevice(null);
    }
  };

  useEffect(() => {
    if (open) {
      fetchData();
      fetchDevices();
      // Detect existing push subscription to enable/disable push toggles
      if ('serviceWorker' in navigator && 'PushManager' in window) {
        navigator.serviceWorker.ready
          .then((registration) => registration.pushManager.getSubscription())
          .then((sub) => setHasPushSubscription(!!sub))
          .catch(() => setHasPushSubscription(false));
      } else {
        setHasPushSubscription(false);
      }
    }
  }, [open]);

  const ensurePushSubscription = async () => {
    try {
      if (!('serviceWorker' in navigator) || !('PushManager' in window) || typeof Notification === 'undefined') {
        showSnackbar('Push non supportate dal browser', 'error');
        return false;
      }

      const token = localStorage.getItem('token');
      if (!token) {
        showSnackbar('Token di autenticazione non trovato', 'error');
        return false;
      }

      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        showSnackbar('Permesso notifiche negato', 'warning');
        return false;
      }

      setSubscribing(true);
      const registration = await navigator.serviceWorker.register('/service-worker.js', { scope: '/' });

      const vapidRes = await fetch(`${process.env.REACT_APP_DOMAIN}/notifications/api/vapid-public-key/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!vapidRes.ok) {
        setSubscribing(false);
        showSnackbar('Impossibile ottenere la chiave VAPID', 'error');
        return false;
      }
      const { public_key } = await vapidRes.json();
      const convertedKey = await urlBase64ToUint8Array(public_key);

      // Always unsubscribe first to force a fresh subscription
      // This prevents stale/expired subscriptions from being reused
      const existingSubscription = await registration.pushManager.getSubscription();
      if (existingSubscription) {
        console.log('[NotificationSettings] Removing existing subscription to create fresh one');
        await existingSubscription.unsubscribe();
      }

      const pushSubscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: convertedKey
      });

      const subscriptionData = {
        endpoint: pushSubscription.endpoint,
        keys: {
          p256dh: btoa(String.fromCharCode(...new Uint8Array(pushSubscription.getKey('p256dh')))),
          auth: btoa(String.fromCharCode(...new Uint8Array(pushSubscription.getKey('auth'))))
        },
        device_id: getDeviceId(),
        device_name: getDeviceName()
      };

      const saveRes = await fetch(`${process.env.REACT_APP_DOMAIN}/notifications/api/subscription/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(subscriptionData)
      });
      if (!saveRes.ok) {
        await pushSubscription.unsubscribe();
        setSubscribing(false);
        try {
          const err = await saveRes.json();
          showSnackbar(err.error || 'Errore nel salvataggio della sottoscrizione', 'error');
        } catch {
          showSnackbar('Errore nel salvataggio della sottoscrizione', 'error');
        }
        return false;
      }

      setHasPushSubscription(true);
      setSubscribing(false);
      showSnackbar('Notifiche push abilitate', 'success');
      return true;
    } catch (e) {
      setSubscribing(false);
      showSnackbar(e.message || 'Errore attivazione push', 'error');
      return false;
    }
  };

  const handleTestPush = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        showSnackbar('Token di autenticazione non trovato', 'error');
        return;
      }

      // Ensure we have a browser subscription before testing (always attempt)
      const ok = await ensurePushSubscription();
      if (!ok) return;

      setTesting(true);
      const res = await fetch(`${process.env.REACT_APP_DOMAIN}/notifications/api/test-send/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          title: 'Test Frontend',
          message: 'Push inviata dal frontend',
          url: window.location.origin,
          tag: 'frontend-test',
          create_notification: true
        })
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        showSnackbar(data.message || data.error || 'Errore invio notifica di test', 'error');
        return;
      }
      showSnackbar(`Notifica inviata (${data.sent}/${data.total})`, 'success');
    } catch (e) {
      showSnackbar(e.message || 'Errore invio notifica di test', 'error');
    } finally {
      setTesting(false);
    }
  };

  const getCurrentSetting = (appValue) => {
    const settingsArray = Array.isArray(settings) ? settings : [];
    return settingsArray.find(setting => setting.app === appValue) || {
      app: appValue,
      push_enabled: false,
      email_enabled: false
    };
  };

  const updateSetting = (appValue, field, value) => {
    const updatedSettings = [...settings];
    const existingIndex = updatedSettings.findIndex(setting => setting.app === appValue);
    
    if (existingIndex >= 0) {
      updatedSettings[existingIndex] = {
        ...updatedSettings[existingIndex],
        [field]: value
      };
    } else {
      updatedSettings.push({
        app: appValue,
        push_enabled: field === 'push_enabled' ? value : false,
        email_enabled: field === 'email_enabled' ? value : false
      });
    }
    
    setSettings(updatedSettings);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      
      if (!token) {
        throw new Error('Token di autenticazione non trovato');
      }

      // Auto-subscribe if user enabled push but no subscription exists
      const wantsPush = (settings || []).some(s => s.push_enabled);
      if (wantsPush && !hasPushSubscription) {
        const ok = await ensurePushSubscription();
        if (!ok) {
          setSaving(false);
          return;
        }
      }

      //console.log('Saving settings:', settings);

      const response = await fetch(`${process.env.REACT_APP_DOMAIN}/notifications/api/settings/bulk_update/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          settings: settings.map(setting => ({
            app: setting.app,
            push_enabled: setting.push_enabled,
            email_enabled: setting.email_enabled
          }))
        })
      });

      if (!response.ok) {
        let errorMessage = 'Errore nel salvataggio delle impostazioni';
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorData.detail || errorMessage;
        } catch (parseError) {
          errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();
      //console.log('Settings saved successfully:', result);
      showSnackbar('Impostazioni salvate con successo', 'success');
      
    } catch (err) {
      console.error('Save error:', err);
      showSnackbar(err.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const renderNotificationIcon = (type) => {
    const iconProps = { 
      size: 16, 
      style: { marginRight: 8, color: darkMode ? '#4CC9F0' : '#4361EE' } 
    };
    
    switch (type) {
      case 'push':
        return <Bell {...iconProps} />;
      case 'email':
        return <Mail {...iconProps} />;
      default:
        return null;
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        disableEnforceFocus
        disableAutoFocus
        PaperProps={{
          sx: {
            backgroundColor: darkMode ? '#1E1E1E' : '#FFFFFF',
            color: darkMode ? '#FFFFFF' : '#333333',
            border: `1px solid ${darkMode ? '#ffffff20' : '#00000020'}`,
          }
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Settings size={24} style={{ marginRight: 12, color: darkMode ? '#4CC9F0' : '#4361EE' }} />
            Impostazioni Notifiche
          </Box>
        </DialogTitle>
        
        <DialogContent>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          ) : (
            <Box>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                Configura le tue preferenze di notifica per ogni applicazione
              </Typography>
              
              {apps.map((app, index) => {
                const currentSetting = getCurrentSetting(app.value);
                
                return (
                  <Card 
                    key={app.value} 
                    sx={{ 
                      mb: 2,
                      backgroundColor: darkMode ? '#2A2A2A' : '#F8F9FA',
                      border: `1px solid ${darkMode ? '#ffffff10' : '#00000010'}`
                    }}
                  >
                    <CardContent>
                      <Typography variant="h6" sx={{ mb: 2 }}>
                        {app.label}
                      </Typography>
                      
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <FormControlLabel
                            control={
                          <Switch
                                checked={currentSetting.push_enabled}
                                onChange={async (e) => {
                                  const enabled = e.target.checked;
                                  if (enabled && !hasPushSubscription) {
                                    const ok = await ensurePushSubscription();
                                    if (!ok) {
                                      return; // keep disabled if subscription fails
                                    }
                                  }
                                  updateSetting(app.value, 'push_enabled', enabled);
                                }}
                                color="primary"
                                disabled={subscribing}
                              />
                            }
                            label={
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                {renderNotificationIcon('push')}
                                Push
                              </Box>
                            }
                          />
                        </Grid>
                        
                        <Grid item xs={12} sm={6}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={currentSetting.email_enabled}
                                onChange={(e) => updateSetting(app.value, 'email_enabled', e.target.checked)}
                                color="primary"
                              />
                            }
                            label={
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                {renderNotificationIcon('email')}
                                Email
                              </Box>
                            }
                          />
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                );
              })}
            </Box>
          )}

          {/* Registered Devices Section */}
          {!loading && (
            <Box sx={{ mt: 3 }}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Smartphone size={20} />
                Dispositivi Registrati
              </Typography>
              
              {loadingDevices ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : devices.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Nessun dispositivo registrato
                </Typography>
              ) : (
                <List dense>
                  {devices.map((device) => (
                    <ListItem 
                      key={device.id}
                      sx={{
                        bgcolor: device.is_current 
                          ? (darkMode ? 'rgba(76, 201, 240, 0.1)' : 'rgba(67, 97, 238, 0.1)')
                          : 'transparent',
                        borderRadius: 1,
                        mb: 0.5
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        {device.device_name?.toLowerCase().includes('mobile') || 
                         device.device_name?.toLowerCase().includes('android') ||
                         device.device_name?.toLowerCase().includes('ios') 
                          ? <Smartphone size={20} />
                          : <Monitor size={20} />
                        }
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {device.device_name || 'Dispositivo sconosciuto'}
                            {device.is_current && (
                              <Chip 
                                label="Questo dispositivo" 
                                size="small" 
                                color="primary"
                                sx={{ height: 20, fontSize: '0.7rem' }}
                              />
                            )}
                          </Box>
                        }
                        secondary={device.last_used 
                          ? `Ultimo utilizzo: ${new Date(device.last_used).toLocaleDateString('it-IT', {
                              day: '2-digit',
                              month: 'short',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}`
                          : 'Mai utilizzato'
                        }
                      />
                      <ListItemSecondaryAction>
                        <IconButton
                          edge="end"
                          onClick={() => handleDeleteDevice(device.id)}
                          disabled={deletingDevice === device.id || device.is_current}
                          title={device.is_current ? 'Non puoi rimuovere il dispositivo corrente' : 'Rimuovi dispositivo'}
                          sx={{ 
                            color: device.is_current 
                              ? 'text.disabled' 
                              : 'error.main',
                            '&:hover': {
                              bgcolor: device.is_current ? 'transparent' : 'error.light'
                            }
                          }}
                        >
                          {deletingDevice === device.id 
                            ? <CircularProgress size={20} />
                            : <Trash2 size={18} />
                          }
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>
          )}
        </DialogContent>
        
        <DialogActions>
          <Button 
            onClick={handleTestPush}
            variant="outlined"
            disabled={loading || testing}
            sx={{ mr: 'auto' }}
          >
            {testing ? <CircularProgress size={20} /> : 'Test Notifica'}
          </Button>
          <Button 
            onClick={onClose}
            sx={{ color: darkMode ? '#FFFFFF' : '#333333' }}
          >
            Annulla
          </Button>
          <Button 
            onClick={handleSave}
            variant="contained"
            disabled={loading || saving}
            sx={{
              backgroundColor: darkMode ? '#4CC9F0' : '#4361EE',
              '&:hover': {
                backgroundColor: darkMode ? '#3BA5C7' : '#3551CC'
              }
            }}
          >
            {saving ? <CircularProgress size={20} /> : 'Salva'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default NotificationSettings; 